// src/utils/monthlyReconciliation.js
//
// Column / totals helpers for the read-only Monthly Reconciliation summary
// table that lives under the Performance Report form. Mirrors the shape of
// `utils/associateFee.js`: the page composes the table itself and just asks
// this module for column definitions and aggregate totals.
//
// The rows we receive come from `useMonthlyCompensationsByClinic`, where
// each numeric field is wrapped as `{ total, breakdown: [...] }` (matching
// the same `existingReport.<field>?.total` reads the form already does).
// We accept both the wrapped and flat shapes so the helpers stay tolerant.

import { formatMoney } from "./format";
import { normalizeTypeForUi } from "./utils";
import { APPROVAL_STEPS, isApprovalApproved } from "./associateFee";
import { PROFIT_COLORS, DEDUCTION_COLORS } from "./columnColors";

// Canonical list of fields shown in the table. Order = display order.
// `field` is the key on the API row. `hideKey` matches the parent's
// `hiddenColumns` prop (same vocabulary the form already uses).
// `colorGroup` controls the column colour scheme, mirroring AssociateFee:
//   - "profit"     -> sky blue (PROFIT_COLORS)
//   - "deduction"  -> slate    (DEDUCTION_COLORS)
//   - "plain"      -> default cell colour
export const MONTHLY_RECON_FIELDS = [
  { key: "production", label: "PRODUCTION", field: "production", colorGroup: "profit" },
  { key: "collection", label: "COLLECTION", field: "collection", colorGroup: "profit" },
  {
    key: "lab_cost",
    label: "LAB COSTS",
    field: "lab_cost",
    hideKey: "lab_costs",
    colorGroup: "deduction",
  },
  {
    key: "pdf_lab_cost",
    label: "PDF LAB COSTS",
    field: "pdf_lab_cost",
    hideKey: "lab_costs",
    colorGroup: "deduction",
  },
  {
    key: "material_cost",
    label: "MATERIAL",
    field: "material_cost",
    hideKey: "material_costs",
    colorGroup: "deduction",
  },
  {
    key: "invisalign_cost",
    label: "INVISALIGN",
    field: "invisalign_cost",
    hideKey: "invisalign_costs",
    colorGroup: "deduction",
  },
  {
    key: "anesthesia_fee",
    label: "ANESTHESIA",
    field: "anesthesia_fee",
    hideKey: "anesthesia_fee",
    colorGroup: "deduction",
  },
  {
    key: "anesthesiologist_fee",
    label: "ANESTHESIOLOGIST",
    field: "anesthesiologist_fee",
    hideKey: "anesthesiologist_fee",
    colorGroup: "deduction",
  },
  {
    key: "facility_fee",
    label: "FACILITY",
    field: "facility_fee",
    hideKey: "facility_fee",
    colorGroup: "deduction",
  },
  { key: "botox", label: "BOTOX", field: "botox", hideKey: "botox", colorGroup: "deduction" },
  {
    key: "production_adjustment",
    label: "PRODUCTION ADJ.",
    field: "production_adjustment",
    hideKey: "production_adjustment",
    colorGroup: "deduction",
  },
  {
    key: "collection_adjustment",
    label: "COLLECTION ADJ.",
    field: "collection_adjustment",
    hideKey: "collection_adjustment",
    colorGroup: "deduction",
  },
  {
    key: "hours_worked",
    label: "HOURS",
    field: "hours_worked",
    money: false,
    formatter: (v) => (Number(v) ? Number(v).toFixed(2) : "0.00"),
  },
];

const COLOR_GROUP_CLASSES = {
  profit: {
    header: PROFIT_COLORS.header,
    cell: PROFIT_COLORS.cell,
    na: "text-palette-sky-heading/70",
  },
  deduction: {
    header: DEDUCTION_COLORS.header,
    cell: DEDUCTION_COLORS.cell,
    na: "text-palette-gray-placeholder",
  },
};

const toNum = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Pull the numeric value for `field` off a monthly-compensation row.
 * Handles both `{ total: <number> }` (list endpoint) and flat numeric
 * shapes so the same column works regardless of payload shape.
 */
export function getMonthlyReconValue(row, field) {
  if (!row) return 0;
  const raw = row[field];
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    return toNum(raw.total);
  }
  return toNum(raw);
}

/**
 * Sum every numeric field across `rows`. Returned object is keyed by
 * `field` (not `key`) so the caller can index it directly with a column
 * config entry's `field`.
 */
export function computeMonthlyReconTotals(rows) {
  const totals = {};
  for (const f of MONTHLY_RECON_FIELDS) totals[f.field] = 0;
  for (const r of rows || []) {
    for (const f of MONTHLY_RECON_FIELDS) {
      totals[f.field] += getMonthlyReconValue(r, f.field);
    }
  }
  return totals;
}

/**
 * Build the columns array for the Monthly Reconciliation table.
 *
 * Renders a footer-style "TOTAL" row when `row.__isTotal` is true (so the
 * caller can pass a synthetic totals row through `<PaginatedTable footerRow>`
 * or as the last item in `data`).
 *
 * @param {Object} opts
 * @param {string[]} [opts.hiddenColumns] - parent-supplied hide list.
 */
export function getMonthlyReconColumns({ hiddenColumns = [] } = {}) {
  const hidden = new Set(hiddenColumns);

  const practitionerCol = {
    key: "practitioner_name",
    header: "PRACTITIONER",
    width: 180,
    render: (row) => {
      if (row?.__isTotal) {
        return <span className="text-palette-slate-text">TOTAL</span>;
      }
      return <span className="text-palette-slate-text">{row?.practitioner_name || "—"}</span>;
    },
  };

  const typeCol = {
    key: "practitioner_type",
    header: "TYPE",
    width: 70,
    align: "center",
    render: (row) => {
      if (row?.__isTotal) return null;
      const ui = normalizeTypeForUi(row?.practitioner_type);
      if (!ui) return <span className="text-palette-gray-placeholder text-xs">—</span>;
      const isDds = ui === "DDS";
      const cls = isDds
        ? "bg-palette-indigo-muted text-palette-indigo-text border-palette-indigo-border"
        : "bg-palette-emerald-muted text-palette-emerald-text border-palette-emerald-border";
      return (
        <span
          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${cls}`}
        >
          {ui}
        </span>
      );
    },
  };

  const numericCols = MONTHLY_RECON_FIELDS.filter((f) => !(f.hideKey && hidden.has(f.hideKey))).map(
    (f) => {
      const colors = COLOR_GROUP_CLASSES[f.colorGroup];
      return {
        key: f.key,
        header: f.label,
        width: 110,
        align: "right",
        headerClassName: colors?.header,
        cellClassName: colors?.cell,
        render: (row) => {
          // Footer row stores aggregated numbers under the same `field` so we
          // can format them with the same formatter the column uses.
          const value = row?.__isTotal ? toNum(row[f.field]) : getMonthlyReconValue(row, f.field);
          const formatter = f.formatter || (f.money === false ? toNum : formatMoney);
          const display = formatter(value);

          if (row?.__isTotal) {
            return <span className="text-palette-slate-text">{display}</span>;
          }
          if (value === 0) {
            return (
              <span className={`text-xs ${colors?.na || "text-palette-gray-placeholder"}`}>
                N/A
              </span>
            );
          }
          if (f.key === "pdf_lab_cost") {
            const labCost = getMonthlyReconValue(row, "lab_cost");
            const difference = value - labCost;
            const isMismatch = Math.round(value * 100) !== Math.round(labCost * 100);
            if (isMismatch) {
              return (
                <span
                  className="font-medium text-palette-rose-heading"
                  title={`PDF ${formatMoney(value)} vs Lab Costs ${formatMoney(
                    labCost,
                  )}; difference ${formatMoney(difference)}`}
                >
                  {display}
                </span>
              );
            }
          }
          return <span className="text-palette-slate-text">{display}</span>;
        },
      };
    },
  );

  // Read-only approval indicator: AM → PR → Admin pills, mirroring
  // the Associate Fee table's `ApprovalStepper` but without any of the
  // approve / request-reset interactions (this view is informational only).
  const STEP_SHORT_LABEL = { am: "AM", payroll: "PR", admin: "Admin" };
  const approvalCol = {
    key: "approval",
    header: "APPROVAL",
    width: 200,
    align: "center",
    headerClassName: "text-center",
    render: (row) => {
      if (row?.__isTotal) return null;
      return (
        <div className="flex items-center justify-center gap-1">
          {APPROVAL_STEPS.map((step, idx) => {
            const slot = row?.[step.field];
            const approved = isApprovalApproved(slot);
            const cls = approved
              ? "bg-palette-emerald-muted text-palette-emerald-text border-palette-emerald-border"
              : "bg-palette-gray-subtle text-palette-gray-secondary border-palette-gray-divider";
            const short = STEP_SHORT_LABEL[step.key] || step.label;
            const tip = `${step.label}: ${approved ? "Approved" : "Pending"}${
              approved && slot?.user ? ` — ${slot.user}` : ""
            }`;
            return (
              <span key={step.key} className="inline-flex items-center gap-1">
                <span
                  title={tip}
                  className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap ${cls}`}
                >
                  {approved ? "\u2713 " : ""}
                  {short}
                </span>
                {idx < APPROVAL_STEPS.length - 1 && (
                  <span className="text-palette-gray-placeholder text-[10px]">{"\u2192"}</span>
                )}
              </span>
            );
          })}
        </div>
      );
    },
  };

  return [practitionerCol, typeCol, ...numericCols, approvalCol];
}

/**
 * Build the synthetic "TOTAL" row consumed by either `<DataTable>`'s
 * footerRow prop or as a tail entry in `data`. The shape mirrors a real
 * row so the column renderers Just Work — the only difference is the
 * `__isTotal` flag they branch on for the bold styling.
 */
export function buildMonthlyReconTotalRow(rows) {
  const totals = computeMonthlyReconTotals(rows);
  return {
    __isTotal: true,
    _id: "__monthly_recon_total__",
    practitioner_name: "TOTAL",
    ...totals,
  };
}
